<template>
    <div style="padding-bottom:1rem;">
      <div class="text-center animate__animated animate__zoomIn heading blue--text">
        <p>{{"Land Location: Why Strategic Land Location Matters"}}</p>
      </div>
      <v-row class="no-gutters">
        <v-col class="col-12 px-2">
          <span>
            Land is homogeneous across large tracks; equally sized parcels of land should therefore, ideally, be worth the same, except they are not. 
            Different plots of land may differ in their value despite being of the same size, fertility and climate. 
            This difference is informed by several factors such as its size, proximity to amenities, underground natural resources but chiefly by its location. 
            This is the reason why an acre in the leafy suburbs of Nairobi is several folds more expensive that an acre in, say, Mandera. 
            Clichés get to be clichés since they have been deemed worth repeating having proven their truthfulness; thus, ‘location, location, location’ is as true as day. 
            This drives our discussion topic today: 
            <b><i>why is land location important? </i></b>
            <ul class="ml-4">
              <li class="ml-4">Why is location important in deciding where to invest?</li>
              <li class="ml-4">Factors that show ideal location for the land investment.</li>
            </ul>
          </span>
        </v-col>
        <v-col class="col-12 px-2">
          <p class="blue--text mt-2 sub-heading">{{"Why Location is Vital"}}</p>
        </v-col>
        <v-col class="col-12 px-2">
          <span>
            Location is important primarily because it is the only land factor that remains constant. 
            Location directly influences the return on investment for commercial lands by dictating the cost of factors of production and the availability of market and supporting resources. 
            Varying intended uses of land affect the location selection.  For commercial purposes, the location will be guided by space availability (factories), ready market for the goods, labor availability, 
            cost of transportation and logistics from the premise etc. Location therefore plays a crucial part in the profit margins to be acquired. 
          </span>
          <span>
            Location also plays a pivotal role in selecting personal use land investment. 
            Land purchased for personal use is primarily for residence; therefore, you buy where you will live and settle. 
            The location of this personal use land will, in the long run influence one’s lifestyle. 
            Admittedly, different people prefer different lifestyles, with some opting for country life while others, uptown suburbs; however, despite these differences, everyone wishes for a lifestyle with the 
            minimum possible chaos. As such, a prime residential land should provide appropriate social amenities, good transportation, a culture one desires among other factors.
          </span>
        </v-col>
        <v-col class="col-12 px-2 d-flex justify-center">
          <v-img class="rounded lighten-4"
          :src="require(`../assets/location.jpg`)"
          />
        </v-col>
        <v-col class="col-12 px-2">
          <span>
            The next section takes a look at some of the factors one should consider when assessing the viability of both personal use and commercial land investment’s location.
          </span>  
        </v-col>
        <v-col class="col-12 px-2">
          <p class="blue--text mt-2 sub-heading">{{"Factors That Inform a Good Location"}}</p>
        </v-col>
        <v-col class="col-12 px-2">
          <h4 class="ml-3 mt-1 blue--text">{{"Social Amenities"}}</h4>
        </v-col>
        <v-col class="col-12 px-2">
          <span>
            We, humans, are social beings and as such are inclined towards situations that foster our sociability. 
            The availability of social amenities in the general vicinity makes land more popular, for both commercial and residential uses. 
            Amenities such as shopping malls and centers, good schools and hospitals and places of worship play a part in forming a good lifestyle for those investing for personal use. 
            This in turn creates an influx of population in the area which subsequently increases the area’s available market size making commercial use investments more sound in the area. 
            The Diani Crystal Gardens’ project by Adili Real Estate in Ukunda is optimally location with regards to social amenities being surrounded by 
            <b>a ton of top-notch hotels, learning institutions and a university, hospitals, shopping malls, community football grounds, </b>
            and  most natable,  it is also only
            <b> 6KM off Diani White Sands Beaches.</b>
          </span>
        </v-col>
        <v-col class="col-12 px-2">
          <h4 class="ml-3 mt-1 blue--text">{{"Accessibility"}}</h4>
        </v-col>
        <v-col class="col-12 px-2">
          <span>
            Time is about the only investment that one cannot get back, as such, people are very selfish with it. 
            Therefore, commute time to the property location becomes a foundation consideration when picking land to invest in. 
            People acquiring land for residence should, and do, look for lands that are easily accessible for their places of operation and social amenities.  
            Commercial lands, on the other hand should be easily accessible to the market. 
            This might be the backbone of the growth of popularity in mixed-use property development projects where one can live, play and work in the same general area. 
            The Diani Crystal Gardens is favored in this respect being only a 
            <b>15-minute drive to the Ukunda Airstrip.</b> It is also a meager <b>400M off tarmac.</b>
          </span>
        </v-col>
        <v-col class="col-12 px-2">
          <h4 class="ml-3 mt-1 blue--text">{{"Infrastructure"}}</h4>
        </v-col>
        <v-col class="col-12 px-2">
          <span>
            It is virtually impossible, or at least very expensive, for an investor to put up all the necessary infrastructure to bring value to his land. 
            It is therefore no brainer that lands that are close to vital infrastructure fetch more than those that do not; despite these lands being more expensive, they provide a greater value in the long run. 
            The primary infrastructures required in the modern properties industry are water and sewerage system, electricity, internet, and road and transport networks, among others. 
            The Diani Crystal Gardens have access to water and sewerage services from the Mombasa Water Supply and Sanitation company, and electricity is in its vicinity as well. 
            It is less than a kilometer from the main tarmac road which connects it to the SGR station, the Ukunda airstrip, the ferry among other transport systems, and the almost-complete 
            <b>Dongo Kundu Special Economic Zone</b>
          </span>
        </v-col>
        <v-col class="col-12 px-2">
          <h4 class="ml-3 mt-1 blue--text">{{"Conclusion"}}</h4>
        </v-col>
        <v-col class="col-12 px-2">
          <span>
            In conclusion, you should pay close heed to the location of the prospective land before investing. 
            Having your land investment in a prime location would; for personal use lands, provide you with the desired lifestyle and reduce social chaos, for commercial use land, it would provide an optimal 
            interaction between cost of labor and market availability ensuring a lucrative return on investment. The chief factors that dictate the suitability of land location are its closeness to social amenities, 
            accessibility and infrastructure. Our latest project in Ukunda, the Diani Crystal Gardens 
            <b>checks all these boxes. </b>
            It is close to social amenities such as hotels and the Diani white Sands beach, it is accessible being 400M off tarmac and 15 minutes from the Ukunda airstrip, it also has an access to vital 
            infrastructure such as water, electricity and next to a tarmacked road. Buying this land would be a well-informed investment and you only get to wait for 
            <b>3-6 months </b>
            for your title deed. To talk to us about the land and our flexible payment plan contact us via 
            <a  href="tel: +254720244744">
              <span>
                0720 244 744
              </span>
            </a>
            or  AdiliRealEstate on all social media platforms.
          </span>
        </v-col>
      </v-row>
    </div>
  </template>
  <script>
  export default {
    name: 'landLocationBlog',
    data(){
      return{
        contact_data: {}
      }
    },
    methods: {
      go(code){
        window.location = code
      },
      goToSection(element_id){
        this.go('/' + element_id)
      },
      buyNow(){
        let url = "https://wa.me/+254720244744?text=I%20want%20to%20buy%20this%20land%20now!"
        window.open(url,'_blank')
      }
    }
  }
  </script>
  